/**
 * Spatial Query Dialog
 *
 * Material-UI dialog for configuring spatial queries.
 * Supports all spatial relationship types and displays results.
 *
 * **Validates: Requirements 26.1, 26.2, 26.4, 26.6**
 */

import React, { useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState, AppDispatch } from '../../store';
import type { DistanceUnits, SpatialRelationship } from '../../services/SpatialAnalysisEngine';
import {
  setSpatialQueryTargetLayer,
  setSpatialQueryRelationship,
  setSpatialQueryDistance,
  setSpatialQueryDistanceUnits,
  closeTool,
} from '../../store/slices/spatialAnalysisSlice';

interface SpatialQueryDialogProps {
  open: boolean;
  onExecute: () => void;
  onExportResults: () => void;
}

const RELATIONSHIPS: { value: SpatialRelationship; label: string; description: string }[] = [
  { value: 'contains', label: 'Contains', description: 'Reference feature fully contains target' },
  { value: 'within', label: 'Within', description: 'Target features are within reference' },
  { value: 'intersects', label: 'Intersects', description: 'Features share any portion of space' },
  { value: 'touches', label: 'Touches', description: 'Features share boundary but not interior' },
  { value: 'crosses', label: 'Crosses', description: 'Features cross each other' },
  {
    value: 'within-distance',
    label: 'Within Distance',
    description: 'Target features within specified distance',
  },
];

const DISTANCE_UNITS: { value: DistanceUnits; label: string }[] = [
  { value: 'meters', label: 'Meters' },
  { value: 'kilometers', label: 'Kilometers' },
  { value: 'feet', label: 'Feet' },
  { value: 'miles', label: 'Miles' },
];

export const SpatialQueryDialog: React.FC<SpatialQueryDialogProps> = ({
  open,
  onExecute,
  onExportResults,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { spatialQueryTool, queryResults, isProcessing, error, message } = useSelector(
    (state: RootState) => state.spatialAnalysis
  );
  const layers = useSelector((state: RootState) => state.layers.layers);

  const handleClose = useCallback(() => {
    dispatch(closeTool());
  }, [dispatch]);

  const handleTargetLayerChange = useCallback(
    (e: any) => {
      dispatch(setSpatialQueryTargetLayer(e.target.value));
    },
    [dispatch]
  );

  const handleRelationshipChange = useCallback(
    (e: any) => {
      dispatch(setSpatialQueryRelationship(e.target.value as SpatialRelationship));
    },
    [dispatch]
  );

  const handleDistanceChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(e.target.value);
      if (!isNaN(value)) {
        dispatch(setSpatialQueryDistance(value));
      }
    },
    [dispatch]
  );

  const handleDistanceUnitsChange = useCallback(
    (e: any) => {
      dispatch(setSpatialQueryDistanceUnits(e.target.value as DistanceUnits));
    },
    [dispatch]
  );

  const canExecute =
    spatialQueryTool.referenceFeatureId && spatialQueryTool.targetLayerId && !isProcessing;

  const showDistanceInput = spatialQueryTool.relationship === 'within-distance';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Spatial Query</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Query features based on their spatial relationship to a reference feature.
            {!spatialQueryTool.referenceFeatureId && (
              <strong> Select a feature on the map first.</strong>
            )}
          </Typography>

          {spatialQueryTool.referenceFeatureId && (
            <Alert severity="info" icon={false}>
              Reference feature selected from layer:{' '}
              {layers.find((l) => l.id === spatialQueryTool.referenceLayerId)?.name || 'Unknown'}
            </Alert>
          )}

          <FormControl fullWidth>
            <InputLabel id="spatial-query-relationship-label">Spatial Relationship</InputLabel>
            <Select
              labelId="spatial-query-relationship-label"
              value={spatialQueryTool.relationship}
              onChange={handleRelationshipChange}
              label="Spatial Relationship"
            >
              {RELATIONSHIPS.map((rel) => (
                <MenuItem key={rel.value} value={rel.value}>
                  <Box>
                    <Typography variant="body2">{rel.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {rel.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {showDistanceInput && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Distance"
                type="number"
                value={spatialQueryTool.distance}
                onChange={handleDistanceChange}
                inputProps={{ min: 0, step: 'any' }}
                sx={{ flex: 2 }}
              />
              <FormControl sx={{ flex: 1 }}>
                <InputLabel id="spatial-query-units-label">Units</InputLabel>
                <Select
                  labelId="spatial-query-units-label"
                  value={spatialQueryTool.distanceUnits}
                  onChange={handleDistanceUnitsChange}
                  label="Units"
                >
                  {DISTANCE_UNITS.map((unit) => (
                    <MenuItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <FormControl fullWidth>
            <InputLabel id="spatial-query-target-label">Target Layer</InputLabel>
            <Select
              labelId="spatial-query-target-label"
              value={spatialQueryTool.targetLayerId || ''}
              onChange={handleTargetLayerChange}
              label="Target Layer"
            >
              {layers.map((layer) => (
                <MenuItem key={layer.id} value={layer.id}>
                  {layer.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {error && <Alert severity="error">{error}</Alert>}
          {message && !error && (
            <Alert severity={queryResults.length > 0 ? 'success' : 'info'}>{message}</Alert>
          )}

          {/* Results list */}
          {queryResults.length > 0 && (
            <Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">
                  Results <Chip label={queryResults.length} size="small" color="primary" />
                </Typography>
                <Button size="small" onClick={onExportResults} variant="outlined">
                  Export as Layer
                </Button>
              </Box>
              <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                {queryResults.map((feature, index) => (
                  <ListItem key={feature.id || index}>
                    <ListItemText
                      primary={`Feature ${feature.id || index + 1}`}
                      secondary={`Type: ${feature.geometry.type}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isProcessing}>
          Close
        </Button>
        <Button
          onClick={onExecute}
          variant="contained"
          disabled={!canExecute}
          startIcon={isProcessing ? <CircularProgress size={16} /> : undefined}
        >
          {isProcessing ? 'Querying...' : 'Run Query'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SpatialQueryDialog;
